import type { PageEvent } from '@noths/polaris-client-next-components';
import { minBreakpoints } from '@noths/polaris-client-ribbons-base';
import type { DocumentProps } from 'next/document';

import { PAGE_SIZE } from 'src/adapters/storeToServiceRequest/listings-api/adaptStoreToRequestBody';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { selectPreviousPage } from 'src/redux/products/selectors/selectors';
import { selectPageCategories } from 'src/redux/productsQuery/selectors/selectPageCategories';
import { getPageTrackingContext } from 'src/tracking/utils/getPageTrackingContext';
import type { ReturnedProductObject } from 'src/types/googleAnalytics';
import type { FilterNavType } from 'src/types/navigation';
import { PageType } from 'src/types/navigation';
import { logger } from 'src/utils/serverLogger';
import { transformProductsToGAReturnedProductsArray } from 'src/utils/transformProductsToGAReturnedProductsArray';

export type PageEventCategoryType = 'Category' | 'Partner' | 'Search' | 'Taxonomy';

interface BasePageEvent {
  app_version: string;
  attribution_token?: string;
  bot_caching: 'true' | 'false';
  category: PageEventCategoryType;
  crs_categories?: string;
  crs_filter?: string;
  environment: typeof process.env.NODE_ENV;
  exact_listing_items?: number;
  expanded_listing_items?: number;
  filter_list: string;
  filter_nav_type: FilterNavType | '';
  list_id: string | null;
  list_name: string;
  offset: number;
  react: 'true';
  release_version: string;
  returned_products?: ReturnedProductObject[];
  total_listing_items: number;
}

export interface PageEventObject extends PageEvent {
  page: BasePageEvent;
}

export type PageProps = DocumentProps & { updatedReduxState: ReduxApplicationState };

export const UNDEFINED_PAGE_TYPE_ERROR = 'Page tracking event - Undefined page type';
export const UNKNOWN_PAGE_TYPE_ERROR = 'Page tracking event - Unknown page type';

export const filterNavFilterScript = `
  if (pageObject.page) {
    let filterNavType = 'sticky filter';
    if (window && window.innerWidth >= ${minBreakpoints.M}) {
      filterNavType = 'collapsed sidebar';
    }
    if (window && window.innerWidth >= ${minBreakpoints.L}) {
      filterNavType = 'fixed sidebar';
    }
    pageObject.page.filter_nav_type = filterNavType;
  }
`;

const getAsStringValueOrThrow = (value: number | string | undefined | null) => {
  if (value) {
    return String(value);
  }

  throw new Error(UNKNOWN_PAGE_TYPE_ERROR);
};

const getListId = (state: ReduxApplicationState) => {
  const context = getPageTrackingContext(state);

  if (context.pageIdentifier) {
    return getAsStringValueOrThrow(context.pageIdentifier);
  }

  return getAsStringValueOrThrow('missing list ID');
};

const getListName = (state: ReduxApplicationState) => {
  const { entityName, pageType } = getPageTrackingContext(state);

  switch (pageType) {
    case PageType.Category:
      return getAsStringValueOrThrow('unknown category name');
    case PageType.Taxonomy:
      return getAsStringValueOrThrow('shop');
    case PageType.Search:
    case PageType.Partner:
      return getAsStringValueOrThrow(entityName);
    default:
      throw new Error(UNKNOWN_PAGE_TYPE_ERROR);
  }
};

const getPageEventCategory = (pageType: PageType): PageEventCategoryType => {
  switch (pageType) {
    case PageType.Category:
      return 'Category';
    case PageType.Taxonomy:
      return 'Taxonomy';
    case PageType.Partner:
      return 'Partner';
    case PageType.Search:
      return 'Search';
    default:
      return pageType;
  }
};

/**
 * This is to add the page object immediately into datalayer before
 * the gtm.start tag fires
 */
export const getServerSidePageEventObject = (
  version: string,
  appVersion: string,
  state: ReduxApplicationState,
): PageEventObject => {
  if (!state.navigation.pageType) {
    logger.warn({
      location: 'getServerSidePageEventObject',
      extra: { error: new Error(UNDEFINED_PAGE_TYPE_ERROR) },
    });
    return {} as PageEventObject;
  }

  try {
    const { navigation, products, productsQuery } = state;
    const pageType = navigation.pageType!;
    const pageIdentifier = getListId(state);
    const listName = getListName(state);
    const pageCategories = selectPageCategories(state);

    const { lastPageLoaded, pages, providerAttributionToken, providerMetadata, totalProducts } =
      products;
    const returnedProducts = pages[lastPageLoaded!] ?? [];
    const expandedSearchProducts = returnedProducts.filter((product) => product.isExpandedSearch);
    const hasExpandedSearchResults = expandedSearchProducts.length > 0;
    const exactListingItemsCount = hasExpandedSearchResults
      ? returnedProducts.length - expandedSearchProducts.length
      : totalProducts;
    const offset = selectPreviousPage(state) * PAGE_SIZE;

    return {
      page: {
        bot_caching: state.user.bot ? 'true' : 'false',
        category: getPageEventCategory(pageType),
        environment: process.env.NODE_ENV,
        release_version: version,
        app_version: appVersion,
        filter_list: productsQuery ? productsQuery.facets.map((f) => f.title).join(',') : '',
        // Gets set on page load when nav type is calculated
        filter_nav_type: '',
        list_id: pageIdentifier,
        list_name: listName,
        react: 'true',
        offset: offset,
        total_listing_items: totalProducts || -1,
        ...(hasExpandedSearchResults && {
          exact_listing_items: exactListingItemsCount,
          expanded_listing_items: totalProducts - exactListingItemsCount,
        }),
        ...(providerAttributionToken && { attribution_token: providerAttributionToken }),
        ...(providerMetadata?.filter && { crs_filter: providerMetadata.filter }),
        ...(pageCategories?.[0] && {
          crs_categories: pageCategories[0],
        }),
        ...(pages?.[lastPageLoaded!] && {
          returned_products: transformProductsToGAReturnedProductsArray(pages[lastPageLoaded!]),
        }),
      },
    };
  } catch (error) {
    logger.warn({
      location: 'generatePageEventObject',
      extra: { error },
    });

    return {} as PageEventObject;
  }
};
